import type { AccountProfile } from '@/domain/account/Account'
import type { PromotionLink, TeamMember } from '@/domain/affiliate/Affiliate'
import type { LoginResult as DomainLoginResult } from '@/domain/auth/Auth'
import type {
  DailyPerformance,
  LastMonthRevenue,
  MonthlyEarnings,
} from '@/domain/performance/Performance'
import type { AgentId, ConsumerId } from '@/domain/shared/Brand'
import {
  AccountProfileResponseSchema,
  Last7DaysRevenueResponseSchema,
  LastMonthRevenueResponseSchema,
  LoginResultResponseSchema,
  MonthlyEarningsResponseSchema,
  PromotionLinkResponseSchema,
  TeamMembersResponseSchema,
} from './ApiResponseSchemas'

/**
 * 将 API 响应转换为账户资料 Domain 对象
 */
export function toAccountProfile(data: unknown): AccountProfile {
  const parsed = AccountProfileResponseSchema.parse(data)
  return {
    agent_id: parsed.agent_id as AgentId,
    agent_name: parsed.agent_name,
    nickname: parsed.nickname,
    promotion_code: parsed.promotion_code,
    commission_rate: parsed.commission_rate,
    is_disabled: parsed.is_disabled,
    created_at: parsed.created_at,
  }
}

/**
 * 将 API 响应转换为团队成员 Domain 对象数组
 */
export function toTeamMembers(data: unknown): TeamMember[] {
  const parsed = TeamMembersResponseSchema.parse(data)
  return parsed.consumers.map((item) => ({
    consumer_id: item.consumer_id as ConsumerId,
    nickname: item.nickname,
    joined_at: item.joined_at,
  }))
}

/**
 * 将 API 响应转换为推广链接 Domain 对象
 */
export function toPromotionLink(data: unknown): PromotionLink {
  const parsed = PromotionLinkResponseSchema.parse(data)
  return {
    promotion_link: parsed.promotion_link,
  }
}

/**
 * 将 API 响应转换为月度收益 Domain 对象
 */
export function toMonthlyEarnings(data: unknown): MonthlyEarnings {
  const parsed = MonthlyEarningsResponseSchema.parse(data)
  return {
    monthly_revenue: parsed.monthly_revenue,
  }
}

/**
 * 将 API 响应转换为上月营收 Domain 对象
 */
export function toLastMonthRevenue(data: unknown): LastMonthRevenue {
  const parsed = LastMonthRevenueResponseSchema.parse(data)
  return {
    last_month_revenue: parsed.last_month_revenue,
  }
}

/**
 * 将 API 响应转换为每日表现 Domain 对象数组
 */
export function toDailyPerformanceList(data: unknown): DailyPerformance[] {
  const parsed = Last7DaysRevenueResponseSchema.parse(data)
  return parsed.daily_revenue.map((item) => ({
    date: item.date,
    revenue: item.revenue,
  }))
}

/**
 * 将 API 响应转换为登录结果 Domain 对象
 */
export function toLoginResult(data: unknown): DomainLoginResult {
  const parsed = LoginResultResponseSchema.parse(data)
  return {
    access_token: parsed.access_token,
    agent_id: parsed.agent_id as AgentId,
    duration_seconds: parsed.duration_seconds,
    token_type: parsed.token_type,
  }
}
